define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/dom-class",
    "dojo/dom-style",
    "dojo/has",
    "dojo/keys",
    "dijit/_TemplatedMixin",
    "dijit/_WidgetBase",
    "./ButtonBar",
    "./LayoutChildProperty",
    "./LayoutContainerEvent",
    "./Tab"
], function (declare, event, lang, domClass, domStyle, has, keys, _TemplatedMixin, _WidgetBase, ButtonBar,
             LayoutChildProperty, LayoutContainerEvent, Tab) {

    var TAB_OPENING = "tabOpening";

    /**
     * Specializes the ButtonBar to display "tabs" for a TabContainer.
     */
    var TabBar = declare([ButtonBar], {

        tabOpening: "bottom",
        setGap: 5,
        tabClass: Tab,

        postMixInProperties: function () {
            this.inherited(arguments);
            this.entryExitEdge = this.tabOpening;
        },

        buildRendering: function () {
            this.inherited(arguments);

            domClass.add(this.domNode, "tabBar");
        },

        addTab: function (widget, properties, index, immediate, dragInInfo) {
            var watches = [], tabCount;
            var that = this;
            var tab = new this.tabClass({
                id: this.id + "_" + widget.id,
                label: properties.title,
                toolTip: properties.description,
                closable: properties.closable,
                enabled: properties.isEnabled !== false,
                widget: widget,
                properties: properties,
                checked: properties.get(LayoutChildProperty.IS_SHOWING)
            });
            domClass.add(tab.domNode, this._getClassForOpening(this.tabOpening));
            if (properties.get(LayoutChildProperty.IS_SELECTED)) {
                domClass.add(tab.domNode, "selected");
            }
            if (properties.setId) {
                domClass.add(tab.domNode, "mwSetMember");
            }
            if (this.selectionHighlightingEnabled) {
                watches.push(properties.watch(LayoutChildProperty.IS_SELECTED, function (name, oldValue, newValue) {
                    if (newValue) {
                        domClass.add(tab.domNode, "selected");
                    } else {
                        domClass.remove(tab.domNode, "selected");
                    }
                }));
            }

            // Tie showing property to Tab checked state
            watches.push(tab.watch("checked", function () {
                properties.set(LayoutChildProperty.IS_SHOWING, tab.get("checked"));
            }));

            // Listen for close event from tab and forward
            tab.on(LayoutContainerEvent.CLOSE, function(tab) {
                that.emit(LayoutContainerEvent.CLOSE, {widget: tab.widget, properties: tab.properties});
            });

            // Listen for delete key event to close the tab
            if (tab.closable) {
                tab.on("keyup", lang.hitch(tab, function(evt) {
                    if (evt.keyCode === keys.DELETE) {
                        that.emit(LayoutContainerEvent.CLOSE, {widget: tab.widget, properties: tab.properties});
                    }
                }));
            }

            tab.domNode.addEventListener("focus", function() {
                tab.domNode.classList.add("tabFocused");
            });

            tab.domNode.addEventListener("blur", function() {
                tab.domNode.classList.remove("tabFocused");
            });

            watches.push(properties.watch(LayoutChildProperty.IS_SHOWING, function (name, oldValue, newValue) {
                tab.set("checked", newValue);
            }));
            watches.push(properties.watch(LayoutChildProperty.TITLE, function (name, oldValue, newValue) {
                tab.set("label", newValue);
            }));
            watches.push(properties.watch(LayoutChildProperty.DESCRIPTION, function (name, oldValue, newValue) {
                tab.set("toolTip", newValue);
            }));
            watches.push(properties.watch(LayoutChildProperty.INDEX, function (name, oldValue, newValue) {
                if (newValue) {
                    domClass.remove(tab.domNode, "firstTab");
                } else {
                    domClass.add(tab.domNode, "firstTab");
                }
                if (newValue === that._buttonInfo.length - 1) {
                    domClass.add(tab.domNode, "lastTab");
                } else {
                    domClass.remove(tab.domNode, "lastTab");
                }
            }));
            watches.push(properties.watch(LayoutChildProperty.IS_ENABLED, function (name, oldValue, newValue) {
                tab.set("enabled", newValue);
            }));

            if (dragInInfo) {
                this.dragIn(tab, properties, dragInInfo.mousePoint || dragInInfo.index);
            } else {
                this.addButton(tab, properties, true, index, immediate);
            }

            index = this._indexFromProperties(properties);
            this._buttonInfo[index].unbindChildProperties = function () {
                watches.forEach(function (watch) {
                    watch.remove();
                })
            };
            tabCount = this._buttonInfo.length;
            if (index === tabCount - 1 && tabCount > 1) {
                domClass.remove(this._buttonInfo[tabCount - 2].button.domNode, "lastTab");
            }
            tab.domNode.tabIndex = -1;

            tab.domNode.setAttribute("aria-controls", widget.id);

            widget.domNode.removeAttribute("aria-label");
            widget.domNode.setAttribute("aria-labelledby", tab.id);

            widget.domNode.setAttribute("role", "tabpanel region");

            if (!this.domNode.getAttribute("role")) {
                this.domNode.setAttribute("role", "tablist");
            }

            tab.domNode.setAttribute("role", "tab");
            tab.domNode.setAttribute("aria-selected", tab.checked);

            // labelNode is for documents and titleNode is for tabs in the BottomContainer
            const titleNode = tab.labelNode ? tab.labelNode : tab.titleNode;
            if (tab.id && titleNode) {
                const titleNodeId = tab.id + '_title';
                titleNode.setAttribute('id', titleNodeId);

                // See g2149154. If we do not set aria-labelledby to the id of the titleNode,
                // then the screen reader speaks about the actions button as well when the focus
                // is on the tab. This happens because tab's DOM node did not have any aria label
                // so, screen reader speaks about all the DOM nodes that are inside of tab's DOM
                // node.
                tab.domNode.setAttribute('aria-labelledby', titleNodeId);
            }

            return tab;
        },

        removeTab: function (properties, immediate) {
            var index, buttonInfo, button;
            index = this._indexFromProperties(properties);
            if (index >= 0) {
                buttonInfo = this._buttonInfo[index];
                button = buttonInfo.button;
                buttonInfo.unbindChildProperties();
                this.removeButton(button, function () {
                    button.destroyRecursive();
                }, immediate);
                return button;
            }
        },

        getTabForProperties: function (properties) {
            var tab;
            var index = this._indexFromProperties(properties);
            if (index >= 0) {
                tab = this._buttonInfo[index].button;
            }
            return tab;
        },

        _computeLayout: function () {
            if (this.inherited(arguments)) {
                // Tweak the tab positions such that the open sides spill out of the bar and overlap the
                // tab content wrapper border
                var targetBox;
                for (i = 0; i < this._buttonInfo.length; i++) {
                    targetBox = this._buttonInfo[i].targetBox;
                    switch (this.tabOpening) {
                        case "bottom":
                            targetBox.h += (has("ie") ? 1.5 : 1);
                            break;
                        case "top":
                            targetBox.t -= 1;
                            targetBox.h += 1;
                            break;
                        case "right":
                            targetBox.w += 1;
                            break;
                        case "left":
                            targetBox.l -= 1;
                            targetBox.w += 1;
                    }
                }
                return true;
            } else {
                return false;
            }
        },

        _createPrototypeButton: function () {
            var prototype = new Tab({
                id: this.id +  "prototype",
                label: "prototype"
            });
            domStyle.set(prototype.iconNode, {position: "static"});
            domStyle.set(prototype.labelNode, {position: "static"});
            domStyle.set(prototype.closeNode, {position: "static"});
            return prototype;
        },

        _configurePrototypeButton: function (prototype, button) {
            prototype.set("label", button.get("label") + "###");
            prototype.set("closable", button.get("closable"));
        },

        _setTabOpeningAttr: function (edge) {
            var oldOpenClass, newOpenClass, i;
            if (edge != this.tabOpening) {
                oldOpenClass = this._getClassForOpening(this.tabOpening);
                newOpenClass = this._getClassForOpening(edge);
                this._set("tabOpening", edge);
                this.entryExitEdge = edge;
                // Update classes for all Tabs
                for (i = 0; i < this._buttonInfo.length; i++) {
                    domClass.replace(this._buttonInfo[i].button.domNode, newOpenClass, oldOpenClass);
                }
            }
        },

        _getClassForOpening: function (edge) {
            return edge + "Open";
        },

        /**
         * Overridden to prevent drag start on close button
         */
        _beginButtonDrag: function (buttonInfo, e) {
            if (e.target === buttonInfo.button.closeNode) {
                event.stop(e);
                return;
            }
            this.inherited(arguments);
        },

        _isTruncated: function (buttonInfo) {
            return this.inherited(arguments) || (this.orientation === "vertical" && buttonInfo.currentBox.w < buttonInfo.preferredSize.w);
        },

        getButtonInfo: function () {
            return this._buttonInfo;
        },

        /**
         * Give focus to the checked/showing tab
         */
        focus: function() {
            for (var i = 0; i < this._buttonInfo.length; i++) {
                if (this._buttonInfo[i].button && this._buttonInfo[i].button.checked && this._buttonInfo[i].button.domNode) {
                    this._buttonInfo[i].button.domNode.focus();
                    return;
                }
            }
        }
    });

    TabBar.TAB_OPENING = TAB_OPENING;

    return TabBar;
});